'use client';

import React from 'react';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { PulseButton } from 'src/components/PulseUI';
import { useAppSelector } from 'src/redux/hooks';
import {
  selectSelectedPlay,
  selectFreshFacesData,
} from 'src/redux/slices/strategyFormSlice';
import { LOCATION } from 'src/hooks/filters/constants';
import { useAiGenerator } from 'src/hooks/aiGenerator/useAiGenerator';
import { hasUnfilledCriteriaPlaceholders } from 'src/components/Strategy/Admin/utils/strategyValidation';
import { TargetStrategies } from './components/TargetStrategies';
import { EngageStrategies } from './components/EngageStrategies';

interface ConfigureStepProps {
  onBack: () => void;
  onContinue: () => void;
}

const ConfigureStep: React.FC<ConfigureStepProps> = ({
  onBack,
  onContinue,
}) => {
  const selectedPlay = useAppSelector(selectSelectedPlay);
  const freshFacesData = useAppSelector(selectFreshFacesData);
  const { dynamicCriteria: targetCriteria } = useAiGenerator(LOCATION.STRATEGY);
  const { dynamicCriteria: engageCriteria } = useAiGenerator(
    LOCATION.STRATEGY_ENGAGE
  );
  const { dynamicCriteria: indexEventCriteria } = useAiGenerator(
    LOCATION.PATIENT_JOURNEY_INDEX_EVENT
  );
  const { dynamicCriteria: targetEventCriteria } = useAiGenerator(
    LOCATION.PATIENT_JOURNEY_TARGET_EVENT
  );

  const engageTemplateType = selectedPlay?.engage_template?.type;
  const isPatientJourneyEngage = engageTemplateType === 'PATIENT_JOURNEY';

  // Check if either target or engage criteria has unfilled placeholders
  const hasUnfilledTargetPlaceholders =
    hasUnfilledCriteriaPlaceholders(targetCriteria);

  // For PATIENT_JOURNEY, both index and target event templates must be configured and filled
  const hasUnfilledEngagePlaceholders = isPatientJourneyEngage
    ? !indexEventCriteria?.criteria_template ||
      !targetEventCriteria?.criteria_template ||
      hasUnfilledCriteriaPlaceholders(indexEventCriteria) ||
      hasUnfilledCriteriaPlaceholders(targetEventCriteria)
    : hasUnfilledCriteriaPlaceholders(engageCriteria);

  // Fresh Faces requires a code set selection
  const isFreshFacesTarget =
    selectedPlay?.target_template?.type === 'FRESH_FACES';
  const isFreshFacesMissingCodeSet =
    isFreshFacesTarget && !freshFacesData.code_set;

  const canProceed =
    !hasUnfilledTargetPlaceholders &&
    !hasUnfilledEngagePlaceholders &&
    !isFreshFacesMissingCodeSet;

  return (
    <div className="h-[calc(100vh-22rem)]">
      {/* Main Content */}
      <main
        className="min-w-0 h-full"
        aria-label="Strategy configuration"
        data-testid="configure-main"
      >
        <div className="flex h-full flex-col gap-6 overflow-y-auto rounded-lg border border-neutral-200 bg-white p-6">
          {/* Back Link */}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
            className="inline-flex items-center gap-2 text-sm text-link hover:underline"
            data-testid="back-to-plays-link"
            aria-label="Go back to play selection"
          >
            <Icon icon={faArrowLeft} size="xs" />
            Back to Plays
          </Link>

          {/* Page Header */}
          <div className="flex flex-col gap-1">
            <h1
              className="text-xl font-semibold text-primary"
              data-testid="configure-title"
            >
              Configure: {selectedPlay?.name || 'Strategy'}
            </h1>
            {selectedPlay?.description && (
              <p
                className="text-sm text-secondary"
                data-testid="configure-description"
              >
                {selectedPlay.description}
              </p>
            )}
          </div>

          {/* Targeting Section */}
          <TargetStrategies />

          {/* Engage Section */}
          {selectedPlay?.engage_template && (
            <>
              <div className="h-px w-full bg-neutral-200" />
              <EngageStrategies />
            </>
          )}

          {/* Footer Actions */}
          <div className="mt-auto flex items-center gap-4 justify-between border-t border-neutral-200 pt-6">
            <PulseButton
              variant="outline"
              color="neutral"
              onClick={onBack}
              data-testid="back-button"
              aria-label="Go back"
            >
              Back
            </PulseButton>
            <div className="flex items-center gap-4">
              {!canProceed && (
                <span
                  className="text-sm text-red-500"
                  role="alert"
                  data-testid="placeholder-validation-message"
                >
                  {isFreshFacesMissingCodeSet
                    ? 'Please select a code set before continuing'
                    : 'Please fill in all placeholders before continuing'}
                </span>
              )}
              <PulseButton
                variant="solid"
                color="primary"
                onClick={onContinue}
                disabled={!canProceed}
                endDecorator={faArrowRight}
                data-testid="preview-publish-button"
                aria-label="Preview and publish strategy"
                className="flex-shrink-0 whitespace-nowrap"
              >
                Preview & Publish
              </PulseButton>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConfigureStep;
